import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent } from '../../utils/analytics';
import { useHasChosenView } from '../../utils/viewMode';
import '../../styles/components/AgentWidget.css';

/**
 * Floating chat widget that talks to the Lambda agent.
 *
 * Reads the Lambda Function URL from REACT_APP_AGENT_URL at build time.
 * If the URL isn't set, the widget hides itself (so dev environments
 * don't show a broken button).
 *
 * State stays in this component — refreshing the page starts a new chat.
 * That's intentional for v1: no persistence, no leakage between visitors.
 */

const AGENT_URL = process.env.REACT_APP_AGENT_URL;

// Recruiter / hiring manager focused starter prompts.
// Every item here is clearly answerable from the KB — no questions
// the agent would have to bluff or interpret. When a pill is clicked,
// it's removed and the next unused prompt slides in. Pool exhausted
// → section hides.
//
// Order matters: top of the array shows first.
const SUGGESTIONS_POOL = [
  'Tell me about your work at Bally\'s',
  'Tell me about your work at Schrödinger',
  'What\'s your work authorization?',
  'What\'s your design system experience?',
  'Have you led a team before?',
  'Tell me about your engineering background',
  'How AI-fluent are you?',
  'How do you approach design?',
];

// Persistent topic shortcuts shown above the composer at all times.
// Lets visitors jump out of a deep thread and pivot to a new area
// without having to type. Keep these SHORT so the row never scrolls.
//
// Each entry has:
//   label    — what the pill displays
//   sendAs   — the message text actually sent (can be longer than the label)
//   keywords — substrings used to detect "already discussed" so we hide it
const TOPIC_SHORTCUTS = [
  {
    label: 'Bally\'s',
    sendAs: 'Tell me about your work at Bally\'s',
    keywords: ['ballys', "bally's", 'bally', 'pickem', "pick'em", 'sportsbook'],
  },
  {
    label: 'Schrödinger',
    sendAs: 'Tell me about your work at Schrödinger',
    keywords: ['schrodinger', 'schrödinger', 'maestro', 'livedesign', 'kinase'],
  },
  {
    label: 'What\'s next?',
    sendAs: 'What kind of role are you looking for next?',
    keywords: ['looking for', 'next role', 'open to', 'what role', 'next move', 'looking next'],
  },
  {
    label: 'Design system',
    sendAs: 'What design system work have you done?',
    keywords: ['design system', 'tokens', 'component library'],
  },
];

// Parse <followups>...</followups> block out of an assistant reply.
// Returns { body, followups } where body is the visible text and
// followups is an array of suggestion strings.
const FOLLOWUPS_RE = /<followups>([\s\S]*?)<\/followups>/i;
function parseReply(text) {
  if (!text) return { body: '', followups: [] };
  const match = text.match(FOLLOWUPS_RE);
  if (!match) return { body: text.trim(), followups: [] };
  const followups = match[1]
    .split('\n')
    .map((l) => l.replace(/^[-*]\s*/, '').trim())
    .filter(Boolean)
    .slice(0, 4);
  return {
    body: text.replace(FOLLOWUPS_RE, '').trim(),
    followups,
  };
}

const WELCOME = (
  <>
    <strong>Hi! I'm Chidrupa</strong> — or my AI cousin, anyway.
    <br />
    Ask me about my work, what I'm hunting for next, or whatever's caught
    your eye. I love attention, just keep it about me.
  </>
);

// Tiny safe-ish renderer: handles **bold** and [text](url) only.
// Anything else renders as plain text. We avoid raw HTML to dodge XSS.
function renderInline(text) {
  // Split by markdown link first so URLs don't get bolded
  const parts = [];
  let remaining = text;
  const linkRe = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/;

  while (remaining) {
    const m = remaining.match(linkRe);
    if (!m) {
      parts.push({ type: 'text', value: remaining });
      break;
    }
    if (m.index > 0) parts.push({ type: 'text', value: remaining.slice(0, m.index) });
    parts.push({ type: 'link', label: m[1], url: m[2] });
    remaining = remaining.slice(m.index + m[0].length);
  }

  // Then handle **bold** within text parts
  const boldRe = /\*\*([^*]+)\*\*/g;
  return parts.flatMap((p, i) => {
    if (p.type === 'link') {
      return (
        <a key={`l-${i}`} href={p.url} target="_blank" rel="noopener noreferrer" className="agent-link">
          {p.label}
        </a>
      );
    }
    const segments = [];
    let last = 0;
    let m;
    let idx = 0;
    while ((m = boldRe.exec(p.value)) !== null) {
      if (m.index > last) segments.push(p.value.slice(last, m.index));
      segments.push(<strong key={`b-${i}-${idx++}`}>{m[1]}</strong>);
      last = m.index + m[0].length;
    }
    if (last < p.value.length) segments.push(p.value.slice(last));
    return segments;
  });
}

// Render with paragraph breaks
function renderMessage(text) {
  return text.split(/\n{2,}/).map((para, i) => (
    <p key={i}>{para.split('\n').flatMap((line, j, arr) => {
      const rendered = renderInline(line);
      return j < arr.length - 1 ? [...rendered, <br key={`br-${i}-${j}`} />] : rendered;
    })}</p>
  ));
}

export default function AgentWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]); // [{role: 'user'|'assistant', content: string}]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // True once the visitor has picked a side in the splash chooser
  // (or arrived on a non-home route, etc.). We use this further down
  // to hide the FAB during the 50/50 splash so it doesn't compete
  // with the splash CTA pills.
  const hasChosenView = useHasChosenView();
  // Hide the floating launcher button while the hero CTA ("Talk to me")
  // is on screen — otherwise we'd have two launchers competing for
  // attention. Only relevant on the homepage; other routes don't have
  // the hero CTA, so the FAB stays visible there.
  const [heroVisible, setHeroVisible] = useState(false);
  // Track input focus so we can collapse non-essential UI on mobile
  // (topic bar) — only the input + send remain sticky over the keyboard.
  const [inputFocused, setInputFocused] = useState(false);
  // Desktop-only expand/collapse — lets the visitor get a roomier
  // reading area when answers are long. Ignored on mobile (already
  // full-screen).
  const [expanded, setExpanded] = useState(false);
  // One-shot peek bubble that sits above the FAB the first time it
  // becomes visible — gives visitors a hint there's an AI agent here
  // without auto-opening the panel. Dismisses on click, on opening
  // the chat, or after 6s. Persists dismissal in sessionStorage so it
  // doesn't pop again on subsequent route changes within the session.
  const [peekVisible, setPeekVisible] = useState(false);
  const peekShownRef = useRef(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const location = useLocation();

  // Auto-scroll to bottom as new messages arrive
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  // Focus the input when the panel opens
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Open the chat from anywhere in this component, with analytics. Tracks
  // where the open came from (button click vs. global event from AIWelcome
  // vs. peek bubble) so we can see which entry points actually convert.
  const openChat = (source) => {
    setOpen(true);
    trackEvent('chat_opened', { source });
  };

  // Listen for the global "open agent" event dispatched from the
  // AIWelcome hero (or anywhere else that wants to launch the chat).
  useEffect(() => {
    const handler = () => openChat('hero_cta');
    window.addEventListener('agent:open', handler);
    return () => window.removeEventListener('agent:open', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iOS / Android keyboard handling.
  // Goal: chat background extends across the entire screen (including
  // behind the keyboard) so the portfolio doesn't leak through. The
  // composer pins just above the keyboard, messages stay scrollable.
  //
  // Approach: panel fills the full layout viewport (top:0 to bottom:0,
  // background white). When the keyboard opens, we add padding-bottom
  // equal to the keyboard's height. The padding eats space INSIDE the
  // panel, pushing the composer up. Background extends through the
  // padding (CSS default), so behind the keyboard you see chat-white,
  // not portfolio.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const scroller = scrollRef.current;
    if (!panel) return;
    const vv = window.visualViewport;
    if (!vv) return; // older browsers — bare fallback CSS will handle it

    const update = () => {
      const layoutH = window.innerHeight;
      const padTop = vv.offsetTop;
      const padBottom = Math.max(0, layoutH - vv.offsetTop - vv.height);
      panel.style.paddingTop = `${padTop}px`;
      panel.style.paddingBottom = `${padBottom}px`;
      // Keep the latest message visible as the visible area shrinks/grows
      if (scroller) scroller.scrollTop = scroller.scrollHeight;
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      // Reset so non-mobile breakpoints take over again
      panel.style.paddingTop = '';
      panel.style.paddingBottom = '';
    };
  }, [open]);

  // Watch the hero CTA buttons. While they're in viewport, hide the FAB
  // (redundant). Re-runs on every route change so the observer attaches
  // to whatever is mounted on the current page.
  useEffect(() => {
    setHeroVisible(false); // assume not visible until the observer says otherwise
    // Wait one frame so React has a chance to mount the new route's DOM
    const raf = requestAnimationFrame(() => {
      const target = document.querySelector('.Banner--ai-ctas');
      if (!target) return;
      const observer = new IntersectionObserver(
        ([entry]) => setHeroVisible(entry.isIntersecting),
        { threshold: 0.1 } // a sliver of the CTA visible counts as "visible"
      );
      observer.observe(target);
      // Stash the observer on the function reference so cleanup can find it
      cancelHandle.observer = observer;
    });
    const cancelHandle = { raf, observer: null };
    return () => {
      cancelAnimationFrame(cancelHandle.raf);
      if (cancelHandle.observer) cancelHandle.observer.disconnect();
    };
  }, [location.pathname]);

  // ─── Peek bubble: show once when FAB first becomes visible ───
  // Triggered when heroVisible flips false (FAB now showing) and the
  // panel isn't already open. Skipped if the visitor has dismissed it
  // earlier in this session. The 800ms delay lets the FAB itself
  // finish animating in before the peek lands above it.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (peekShownRef.current) return;
    if (open || heroVisible) return;
    if (sessionStorage.getItem('agent-peek-dismissed')) return;
    // Don't bother on small screens — the FAB takes most of the bottom
    // edge and a peek bubble above it would feel cramped.
    if (window.innerWidth < 700) return;

    peekShownRef.current = true;
    const showTimer = setTimeout(() => setPeekVisible(true), 800);
    return () => clearTimeout(showTimer);
  }, [open, heroVisible]);

  // Auto-collapse the peek after 6 seconds if it's still up.
  useEffect(() => {
    if (!peekVisible) return;
    const t = setTimeout(() => dismissPeek(), 6000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peekVisible]);

  const dismissPeek = () => {
    setPeekVisible(false);
    if (typeof window !== 'undefined') {
      try { sessionStorage.setItem('agent-peek-dismissed', '1'); } catch (e) {}
    }
  };

  // If the widget isn't configured, render nothing.
  // (This must come AFTER all hooks — React enforces hook call order.)
  if (!AGENT_URL) return null;

  // Skip rendering when this page is loaded inside an iframe — most
  // commonly the splash chooser's live previews. Otherwise we get two
  // chat FABs visible at once on the splash screen, which looks broken.
  if (typeof window !== 'undefined' && window.self !== window.top) return null;

  // Hide the FAB during the splash chooser — the splash has its own
  // CTA pills ("Explore my work as a Designer / Developer") and the
  // floating chat button would compete for attention. Only relevant on
  // the home route; other routes don't show the splash, so the FAB
  // stays visible there.
  if (location.pathname === '/' && !hasChosenView) return null;

  const send = async (text) => {
    const userMsg = (text ?? input).trim();
    if (!userMsg || loading) return;

    setInput('');
    setError(null);
    const newMessages = [...messages, { role: 'user', content: userMsg }];
    setMessages(newMessages);
    setLoading(true);

    // GA4: count the user's intent to ask the agent something. Fires once
    // per message regardless of whether the network call succeeds. `source`
    // tells us whether they typed it (`text` is undefined → input field)
    // or clicked a suggestion pill (`text` is a string).
    trackEvent('chat_message_sent', {
      source: typeof text === 'string' ? 'suggestion' : 'typed',
      message_index: newMessages.length,
    });

    try {
      // Strip our local-only fields (`followups`) before sending to the API.
      // Anthropic rejects messages with unknown keys.
      const apiMessages = newMessages.map((m) => ({
        role: m.role,
        content: m.content,
      }));
      const res = await fetch(AGENT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: apiMessages }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || `Server returned ${res.status}`);
        return;
      }
      const { body, followups } = parseReply(data.reply || '(empty response)');

      // Reveal the answer paragraph-by-paragraph instead of dumping
      // the full reply at once — improves readability, feels more
      // like a real reply being typed out.
      const paragraphs = body.split(/\n{2,}/).filter((p) => p.trim());

      // First, push the assistant message with only the first paragraph
      // (or the whole thing if it's a single paragraph).
      setMessages([
        ...newMessages,
        { role: 'assistant', content: paragraphs[0] || body, followups: [] },
      ]);

      // Reveal subsequent paragraphs with a short delay between each
      for (let i = 1; i < paragraphs.length; i++) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 220));
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (!last || last.role !== 'assistant') return prev;
          next[next.length - 1] = {
            ...last,
            content: paragraphs.slice(0, i + 1).join('\n\n'),
          };
          return next;
        });
      }

      // Show the followup pills only after all paragraphs are revealed
      if (followups && followups.length > 0) {
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (!last || last.role !== 'assistant') return prev;
          next[next.length - 1] = { ...last, followups };
          return next;
        });
      }
    } catch (e) {
      setError('Network error. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* One-shot peek bubble. Sits above the FAB the first time it
          appears, fades out on click, on opening the chat, or after 6s.
          Hidden once dismissed (sessionStorage flag prevents reshow). */}
      {peekVisible && !open && !heroVisible && (
        <div
          className="agent-peek"
          role="dialog"
          aria-label="Chat suggestion from Chidrupa's AI agent"
        >
          <button
            type="button"
            className="agent-peek__main"
            onClick={() => { dismissPeek(); openChat('peek'); }}
          >
            <span className="agent-peek__wave" aria-hidden="true">👋</span>
            <span>Hi, I'm Chidrupa's AI cousin — ask me anything</span>
          </button>
          <button
            type="button"
            className="agent-peek__close"
            onClick={dismissPeek}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Floating launcher button.
          Hidden when:
            - the chat panel is already open
            - the hero "Talk to me" CTA is visible (no point in having two) */}
      <button
        className={`agent-fab ${open || heroVisible ? 'agent-fab--hidden' : ''}`}
        onClick={() => { dismissPeek(); openChat('fab'); }}
        aria-label="Open chat with Chidrupa's portfolio agent"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        <span>Chat with me</span>
      </button>

      {/* Panel */}
      {open && (
        <div
          ref={panelRef}
          className={[
            'agent-panel',
            inputFocused ? 'agent-panel--input-focused' : '',
            expanded ? 'agent-panel--expanded' : '',
          ].filter(Boolean).join(' ')}
          role="dialog"
          aria-label="Chat with Chidrupa's portfolio agent"
        >
          <header className="agent-panel__header">
            <div>
              <div className="agent-panel__title">Chidrupa's agent</div>
              <div className="agent-panel__subtitle">AI representative · ask anything</div>
            </div>
            <div className="agent-panel__header-actions">
              {/* Desktop-only expand toggle — hidden on mobile via CSS */}
              <button
                className="agent-panel__expand"
                onClick={() => setExpanded((v) => !v)}
                aria-label={expanded ? 'Shrink chat' : 'Expand chat'}
                title={expanded ? 'Shrink chat' : 'Expand chat'}
              >
                {expanded ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="4 14 10 14 10 20" />
                    <polyline points="20 10 14 10 14 4" />
                    <line x1="14" y1="10" x2="21" y2="3" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="15 3 21 3 21 9" />
                    <polyline points="9 21 3 21 3 15" />
                    <line x1="21" y1="3" x2="14" y2="10" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                  </svg>
                )}
              </button>
              <button
                className="agent-panel__close"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
              >
                ×
              </button>
            </div>
          </header>

          <div className="agent-panel__messages" ref={scrollRef}>
            {messages.length === 0 && (
              <div className="agent-msg agent-msg--assistant">
                <div className="agent-msg__bubble">{WELCOME}</div>
              </div>
            )}

            {/* Persistent starter prompts — top 3 unused from the pool.
                As the visitor sends each one (matched by exact text),
                it's removed and the next unused prompt slides in. */}
            {(() => {
              const used = new Set(
                messages.filter((m) => m.role === 'user').map((m) => m.content)
              );
              const visible = SUGGESTIONS_POOL.filter((s) => !used.has(s)).slice(0, 3);
              if (visible.length === 0) return null;
              return (
                <div className="agent-suggestions">
                  {visible.map((s) => (
                    <button
                      key={s}
                      className="agent-suggestion"
                      onClick={() => send(s)}
                      disabled={loading}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              );
            })()}

            {messages.map((m, i) => {
              const isLastAssistant =
                m.role === 'assistant' &&
                i === messages.length - 1 &&
                !loading;
              return (
                <React.Fragment key={i}>
                  <div className={`agent-msg agent-msg--${m.role}`}>
                    <div className="agent-msg__bubble">
                      {m.role === 'assistant' ? renderMessage(m.content) : m.content}
                    </div>
                  </div>
                  {isLastAssistant && m.followups && m.followups.length > 0 && (
                    <div className="agent-suggestions">
                      {m.followups.map((s) => (
                        <button
                          key={s}
                          className="agent-suggestion"
                          onClick={() => send(s)}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              );
            })}

            {loading && (
              <div className="agent-msg agent-msg--assistant">
                <div className="agent-msg__bubble agent-msg__bubble--typing">
                  <span /><span /><span />
                </div>
              </div>
            )}

            {error && (
              <div className="agent-error">{error}</div>
            )}
          </div>

          {/* Persistent topic shortcuts — always available, lets visitors
              switch threads. Topics already discussed (matched by keyword
              in the user message history) are hidden so the bar stays
              focused on what's NEW to explore. */}
          {(() => {
            const haystack = messages
              .filter((m) => m.role === 'user')
              .map((m) => m.content.toLowerCase())
              .join(' \n ');
            const remaining = TOPIC_SHORTCUTS.filter(
              (t) => !t.keywords.some((k) => haystack.includes(k))
            );
            if (messages.length === 0 || remaining.length === 0) return null;
            return (
              <div className="agent-topic-bar" aria-label="Quick topic shortcuts">
                {remaining.map((t) => (
                  <button
                    key={t.label}
                    className="agent-topic-pill"
                    onClick={() => send(t.sendAs)}
                    disabled={loading}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            );
          })()}

          <form
            className="agent-panel__composer"
            onSubmit={(e) => { e.preventDefault(); send(); }}
          >
            <textarea
              ref={inputRef}
              className="agent-input"
              placeholder="Ask anything..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              rows={1}
              maxLength={4000}
            />
            <button
              className="agent-send"
              type="submit"
              disabled={!input.trim() || loading}
              aria-label="Send"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </form>

          <div className="agent-panel__footnote">
            Replies are AI-generated based on Chidrupa's notes. For anything sensitive, email <a href="mailto:mchidrupa01@gmail.com">mchidrupa01@gmail.com</a>.
          </div>
        </div>
      )}
    </>
  );
}
